import React, {ReactElement, ReactNode, useState} from 'react';
import MinGridTable, {
  ColumnDef
} from '@components/system/MinGridTable/MinGridTable';

import {Typography, makeStyles, Grid} from '@material-ui/core';
import {useSelector} from 'react-redux';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';

import {useTranslation} from 'react-i18next';
import HelperUtils from '@shared/utils/HelperUtils';
import {getPaymentsByGuarantor} from '@shared/services/PaymentHistoryService';
import {AppPermissions, ChargeTypeLabels} from '@shared/constants/Mapping';
import './PatientPaymentCard.scss';
import WithPermission from '@components/system/WithPermission/WithPermission';
import {CommunicationService} from 'src/service/CommunicationService';
import {Subscription} from 'rxjs';

interface PatientPaymentsCardProps {
  guarantorId: number | string;
  clinicId?: number;
  forceUpdate?: number;
  isGuarantorDrawer?: boolean;
}

const PatientPayments = makeStyles(() => ({
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    }
  },
  paymentsContainer: {maxHeight: '15.5rem', overflow: 'auto'},
  secondary: {
    '& .MuiListItemText-secondary': {
      fontSize: '0.9rem',
      overflow: 'hidden',
      width: '100%'
    }
  },
  primary: {
    '& .MuiListItemText-primary': {
      fontSize: '1rem',
      overflow: 'hidden',
      width: '100%'
    }
  },
  listItemText: {
    '& .MuiListItemText-multiline': {
      marginTop: 0,
      marginBottom: 0
    }
  },
  sectionRow: {
    display: 'grid',
    gridTemplateColumns: '100%',
    fontSize: '1.3rem',
    paddingBlock: '1rem'
  },
  DrawerInfo: {
    fontSize: '1.3rem',
    fontWeight: 600,
    paddingBottom: '0.5rem'
  },
  paymentNote: {
    paddingBottom: '1.5rem'
  },
  rightAlignHeader: {
    textAlign: 'right'
  }
}));

const PatientPaymentsCard = (props: PatientPaymentsCardProps): ReactElement => {
  const {guarantorId, clinicId, forceUpdate, isGuarantorDrawer = false} = props;
  const {t} = useTranslation();

  const classes = PatientPayments();

  const [serviceData, setServiceData]: any = useState({});
  const [isLoadingServiceData, setIsLoadingServiceDate] = useState(true);

  const chargeTypeLabels = ChargeTypeLabels.map(item => {
    item.name = t(item.name);
    return item;
  });
  const findChargeTypeObj = (id: string) => {
    const item = chargeTypeLabels.find(item1 => item1.id === id);
    if (item) {
      return item.name;
    }
  };

  // FIXME: ADD PROPER TYPES
  const groupId: any = useSelector((state: any) => state.auth.groupId);

  const selectedClinicId = useSelector(
    (state: any) => state.clinics.selectedClinicId
  );

  const {enqueueSnackbar} = useSnackbar();
  const subscription = React.useRef<Subscription>();

  const getPatientPaymentsDoneByGuarantor = async (): Promise<void> => {
    try {
      const paymentsResponse = await getPaymentsByGuarantor(
        groupId,
        guarantorId,
        (selectedClinicId && selectedClinicId[0]) ?? clinicId
      );
      if (paymentsResponse?.data) {
        setServiceData(paymentsResponse?.data);
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
    } finally {
      setIsLoadingServiceDate(false);
    }
  };
  React.useEffect(() => {
    if (guarantorId) {
      getPatientPaymentsDoneByGuarantor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  React.useEffect(() => {
    // if there is an update available and no state passed
    subscription.current = CommunicationService.getEvent().subscribe(data => {
      if (
        data.action === 'GUARANTOR_PAYMENT_COMPLETED' ||
        data.action === 'PATIENT_COPAY_ADDED'
      ) {
        getPatientPaymentsDoneByGuarantor();
      }
    });
    return () => {
      subscription.current?.unsubscribe();
    };
    // eslint-disable-next-line
  }, []);

  const activeStatementPaymentsColDefs: ColumnDef<any>[] = [
    {
      label: t('PATIENT_DETAILS_CARD.DATE'),
      id: 'date',
      render(row) {
        return HelperUtils.formatDisplayDateTime(row.created_at);
      }
    },
    {
      field: 'clinic_name',
      label: t('PATIENT_DETAILS_CARD.LOCATION'),
      id: 'location'
    },
    {
      field: 'description',
      label: t('PATIENT_DETAILS_CARD.HEADERS.DESCRIPTION'),
      id: 'description'
    },
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.AMOUNT'),
      id: 'amount',
      render(row) {
        return (
          <CurrencyFormatter
            value={row['amount_paid'] ?? '0'}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        );
      },
      colValueCellProps: {
        className: 'table-amount-col'
      },
      colHeaderCellProps: {
        className: classes.rightAlignHeader
      }
    }
  ];

  const pastStatementPaymentsColDefs: ColumnDef<any>[] = activeStatementPaymentsColDefs;

  const copayChargesColDefs: ColumnDef<any>[] = [
    {
      label: t('PATIENT_DETAILS_CARD.DATE'),
      id: 'date',
      render(row) {
        return HelperUtils.formatDisplayDateTime(row.created_at);
      }
    },
    {
      field: 'clinic_name',
      label: t('PATIENT_DETAILS_CARD.LOCATION'),
      id: 'location'
    },
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.CHARGE_TYPE'),
      id: 'payment_status',
      render(row) {
        return findChargeTypeObj(row.payment_status);
      }
    },
    {
      field: 'description',
      label: t('PATIENT_DETAILS_CARD.HEADERS.DESCRIPTION'),
      id: 'description'
    },
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.COMMENTS'),
      id: 'settled_reason',
      render(row) {
        return row?.settled_reason ? (<div>{row?.settled_reason}</div>) : ('-')
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.AMOUNT'),
      id: 'amount',
      render(row) {
        return (
          <CurrencyFormatter
            value={row['amount_paid'] ?? '0'}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        );
      },
      colValueCellProps: {
        className: 'table-amount-col'
      },
      colHeaderCellProps: {
        className: classes.rightAlignHeader
      }
    }
  ];

  const refundColDefs: ColumnDef<any>[] = [
    {
      label: t('PATIENT_DETAILS_CARD.DATE'),
      id: 'date',
      render(row) {
        return HelperUtils.formatDisplayDateTime(row.created_at);
      }
    },
    {
      field: 'clinic_name',
      label: t('PATIENT_DETAILS_CARD.LOCATION'),
      id: 'location'
    },
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.CHARGE_TYPE'),
      id: 'payment_status',
      render(row) {
        return findChargeTypeObj(row.payment_status);
      }
    },
    {
      field: 'reason',
      label: t('PATIENT_DETAILS_CARD.HEADERS.REASON'),
      id: 'reason'
    },
    {
      label: t('PATIENT_DETAILS_CARD.HEADERS.AMOUNT'),
      id: 'amount',
      render(row) {
        return (
          <CurrencyFormatter
            value={row['amount'] ?? '0'}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        );
      },
      colValueCellProps: {
        className: 'table-amount-col'
      },
      colHeaderCellProps: {
        className: classes.rightAlignHeader
      }
    }
  ];

  const renderStatementPayments = (): ReactNode => {
    // Checking payments property exists and is an Array with length  more than 0

    const activeStatementPaymentsRows: any[] = [];
    const pastStatementPaymentsRows: any[] = [];

    if (
      !isLoadingServiceData &&
      Array.isArray(serviceData?.payments) &&
      serviceData.payments.length !== 0
    ) {
      serviceData.payments.forEach((payment: any) => {
        if (payment.is_active) {
          activeStatementPaymentsRows.push(payment);
        } else {
          pastStatementPaymentsRows.push(payment);
        }
      });
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={isGuarantorDrawer ? 12 : 6}>
          {/* Current Statement Payments  */}
          <div>
            <Typography
              className="table-title"
              component={'div'}
              data-test-id="pcl-payments-currentStatementPayments">
              {t('DUE_INVOICES.GUARANTOR_DRAWER.ACTIVE_STATEMENT_PAYMENT')}
            </Typography>
            <MinGridTable
              isLoading={isLoadingServiceData}
              className="current-statement-table-wrapper"
              rows={activeStatementPaymentsRows}
              colDefs={activeStatementPaymentsColDefs}
              noDataText={t(
                'PATIENT_DETAILS_CARD.NO_CURRENT_STATEMENT_PAYMENTS_DATA'
              )}
            />
          </div>
        </Grid>

        <Grid item xs={12} md={isGuarantorDrawer ? 12 : 6}>
          {/* Previous Statement Payment */}
          <div>
            <Typography
              className="table-title"
              component={'div'}
              data-test-id="pcl-payments-pastStatementPayments">
              {t('DUE_INVOICES.GUARANTOR_DRAWER.PAST_STATEMENT_PAYMENT')}
            </Typography>
            <MinGridTable
              isLoading={isLoadingServiceData}
              className="previous-statement-table-wrapper"
              rows={pastStatementPaymentsRows}
              colDefs={pastStatementPaymentsColDefs}
              noDataText={t(
                'PATIENT_DETAILS_CARD.NO_PAST_STATEMENT_PAYMENTS_DATA'
              )}
            />
          </div>
        </Grid>
      </Grid>
    );
  };

  const renderCopayAndOtherChargePayments = () => {
    // Checking payments property exists and is an Array with length  more than 0
    let copayChargeRows = [];
    if (!isLoadingServiceData && Array.isArray(serviceData?.copay_balances)) {
      copayChargeRows = serviceData.copay_balances;
    }
    return (
      <div>
        <Typography
          className="table-title"
          component={'div'}
          data-test-id="pcl-payments-coPayOtherStatementPayments">
          {t('PAYMENTS_LOGIN.COPAY_OR_OTHER_CHARGE_PAYMENTS')}
        </Typography>
        <MinGridTable
          isLoading={isLoadingServiceData}
          className="copay-table-wrapper"
          rows={copayChargeRows}
          colDefs={copayChargesColDefs}
          noDataText={t('PATIENT_DETAILS_CARD.NO_COPAY_CHARGE_PAYMENTS_DATA')}
        />
      </div>
    );
  };

  const renderRefunds = () => {
    // Checking payments property exists and is an Array with length  more than 0
    let refundRows = [];
    if (!isLoadingServiceData && Array.isArray(serviceData?.refunds)) {
      refundRows = serviceData.refunds;
    }
    return (
      <div>
        <Typography
          className="table-title"
          component={'div'}
          data-test-id="pcl-payments-refundStatementPayments">
          {t('PAYMENTS_LOGIN.REFUNDS')}
        </Typography>
        <MinGridTable
          isLoading={isLoadingServiceData}
          className="refund-table-wrapper"
          rows={refundRows}
          colDefs={refundColDefs}
          noDataText={t('PATIENT_DETAILS_CARD.NO_REFUND_DATA')}
        />
      </div>
    );
  };

  return (
    <div className="patient-payment-card-wrapper">
      <Typography
        className={[classes.sectionRow, classes.DrawerInfo].join(' ')}>
        {t('PATIENT_DETAILS_CARD.PAYMENTS')}
      </Typography>
      <Typography className={[classes.paymentNote].join(' ')}>
        {t('PATIENT_DETAILS_CARD.PAYMENTS_NOTE')}
      </Typography>

      <div>
        <div className="content-view">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {renderStatementPayments()}
            </Grid>
            <Grid item xs={12}>
              {renderCopayAndOtherChargePayments()}
            </Grid>
            <Grid item xs={12}>
              {renderRefunds()}
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default WithPermission(
  PatientPaymentsCard,
  AppPermissions.PAYMENT_HISTORY.NAME,
  AppPermissions.PAYMENT_HISTORY.LIST
);
